import { div, fnstate } from '@srfnstack/fntags'

/**
 * Offset-paginated list with infinite scroll.
 *
 * Extracted because four pages had grown their own copy and only one of them —
 * ProductReview — carried the stale-page guard. BulkOperations appends pages
 * with no request token at all, so changing a filter while a page is in flight
 * lets the previous filter's rows land in the new list. Centralising it means a
 * page cannot forget the guard, rather than each remembering independently.
 *
 * The endpoint must return { items, total, limit, offset } — see
 * api/src/private/pagination.js.
 *
 * @param buildUrl (offset, limit) => string. The only genuinely per-page part.
 * @param limit    page size, owned here so callers cannot disagree with the URL.
 * @param onReset  called at the start of a fresh load, before the request —
 *                 for page state that must not outlive a filter change, e.g.
 *                 a selection built against the old rows.
 * @param onPage   (data, isReset) => void, for extra fields a response carries
 *                 beyond items/total (BulkOperations reads a tenants list).
 */
export const createPaginatedList = ({ buildUrl, limit = 20, onReset, onPage }) => {
  const items = fnstate([])
  const total = fnstate(0)
  const loading = fnstate(true)
  const loadingMore = fnstate(false)

  // Monotonic request token. Every fresh load bumps it, so any response that
  // was issued under an older filter is dropped instead of clobbering the list.
  // Checked TWICE per request: after the fetch, and again after .json(), which
  // is itself an await point a filter change can slip through.
  let reqSeq = 0

  const reload = async () => {
    const seq = ++reqSeq
    loading(true)
    if (onReset) onReset()
    try {
      const res = await fetch(buildUrl(0, limit))
      if (seq !== reqSeq) return
      if (!res.ok) return
      const data = await res.json()
      if (seq !== reqSeq) return
      items(data.items || [])
      total(data.total || 0)
      if (onPage) onPage(data, true)
    } catch (err) {
      console.error('paginated reload failed:', err)
    } finally {
      // Only the newest request may clear the spinner — an older one finishing
      // late would otherwise report "done" while the current load is still running.
      if (seq === reqSeq) loading(false)
    }
  }

  const loadMore = async () => {
    if (loadingMore() || loading() || items().length >= total()) return
    const seq = reqSeq // tied to the current filter
    loadingMore(true)
    try {
      const res = await fetch(buildUrl(items().length, limit))
      if (seq !== reqSeq) return
      if (!res.ok) return
      const data = await res.json()
      if (seq !== reqSeq) return
      items([...items(), ...(data.items || [])])
      total(data.total || 0)
      if (onPage) onPage(data, false)
    } catch (err) {
      console.error('paginated loadMore failed:', err)
    } finally {
      loadingMore(false)
    }
  }

  // Place this at the end of the list; scrolling it into view fetches the next
  // page. rootMargin starts the fetch before it is actually visible.
  const sentinel = div({ class: 'h-4' })
  const observer = new IntersectionObserver((entries) => {
    if (entries[0].isIntersecting) loadMore()
  }, { rootMargin: '200px' })
  observer.observe(sentinel)

  return { items, total, loading, loadingMore, reload, loadMore, sentinel }
}
